/**
 * Settings > Legal Entities (M27 PR 2) — /settings/legal-entities.
 *
 * Multi-subsidiary management. Single page composes:
 *   - <DataTable /> list with filters + Excel export + row-click
 *     drawer.
 *   - <EntityDrawer /> with 4 tabs (Identity / Compliance /
 *     Banking / Stats) plus a history-timeline footer reading the
 *     legal_entity_event audit log.
 *   - Add-LE modal modelled on the M7 onboarding step-2 fields.
 *
 * Frozen fields after create: cr_number, region. Soft-delete via
 * archived_at refuses to archive when active employees or contracts
 * exist (409 returned by the server).
 */

import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Banknote,
  Building2,
  FileText,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataTable,
  EmptyState,
  EntityDrawer,
  PageHeader,
} from "../../components/ui";
import { api, ApiError } from "../../lib/api";
import type {
  LegalEntityCreateBody,
  LegalEntityDetail,
  LegalEntityListItem,
} from "../../lib/api";

const _BANDS: Record<string, string> = {
  platinum: "bg-violet-100 text-violet-800",
  "green-high": "bg-emerald-100 text-emerald-800",
  "green-mid": "bg-emerald-100 text-emerald-700",
  "green-low": "bg-amber-100 text-amber-800",
  red: "bg-rose-100 text-rose-800",
};

export function LegalEntitiesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const list = useQuery({
    queryKey: ["legal-entities", includeArchived, search],
    queryFn: () =>
      api.listLegalEntities({
        includeArchived,
        search: search || null,
      }),
  });

  const detail = useQuery({
    queryKey: ["legal-entity", openId],
    queryFn: () => api.getLegalEntity(openId!),
    enabled: !!openId,
  });

  const columns = useMemo<ColumnDef<LegalEntityListItem, unknown>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: (r) => r.name_en,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-text-primary">
              {row.original.name_en}
            </span>
            <span
              dir="rtl"
              className="text-xs text-text-tertiary"
            >
              {row.original.name_ar}
            </span>
          </div>
        ),
      },
      {
        id: "cr",
        header: "CR #",
        accessorKey: "cr_number",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-secondary">
            {row.original.cr_number}
          </span>
        ),
        size: 130,
      },
      {
        id: "gosi",
        header: "GOSI ID",
        accessorFn: (r) => r.gosi_establishment_id ?? "",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-tertiary">
            {row.original.gosi_establishment_id ?? "—"}
          </span>
        ),
        size: 110,
      },
      {
        id: "qiwa",
        header: "Qiwa #",
        accessorFn: (r) => r.qiwa_unified_number ?? "",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-tertiary">
            {row.original.qiwa_unified_number ?? "—"}
          </span>
        ),
        size: 110,
      },
      {
        id: "city",
        header: "City",
        accessorFn: (r) => r.registered_city ?? "",
        cell: ({ row }) => (
          <span className="text-sm text-text-secondary">
            {row.original.registered_city ?? "—"}
          </span>
        ),
        size: 110,
      },
      {
        id: "employees",
        header: "Employees",
        accessorKey: "employee_count",
        cell: ({ row }) => (
          <span className="font-mono text-sm tabular-nums text-text-primary">
            {row.original.employee_count}
          </span>
        ),
        size: 100,
      },
      {
        id: "contracts",
        header: "Contracts",
        accessorKey: "active_contract_count",
        cell: ({ row }) => (
          <span className="font-mono text-sm tabular-nums text-text-primary">
            {row.original.active_contract_count}
          </span>
        ),
        size: 100,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (r) => (r.archived_at ? "archived" : "active"),
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
              row.original.archived_at
                ? "bg-surface-sunken text-text-tertiary"
                : "bg-emerald-100 text-emerald-800"
            }`}
          >
            {row.original.archived_at ? "Archived" : "Active"}
          </span>
        ),
        size: 100,
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("app.le_title")}
        subtitle={t("app.le_subtitle")}
        breadcrumbs={[
          { label: "Settings", href: "/settings" },
          { label: "Legal Entities" },
        ]}
        actions={
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90"
          >
            <Plus size={14} aria-hidden /> New Legal Entity
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          <span>{t("app.le_search_label")}</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("app.le_search_placeholder")}
            className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
          />
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>

      <DataTable
        columns={columns}
        data={list.data ?? []}
        loading={list.isLoading}
        exportFilename="legal-entities"
        onRowClick={(r) => setOpenId(r.id)}
        emptyState={
          <EmptyState
            icon={Building2}
            headline="No legal entities yet"
            description="Your tenant has just one Customer Group. Add a Legal Entity if your group operates through multiple Saudi subsidiaries."
            action={{
              label: "Add Legal Entity",
              onClick: () => setShowAdd(true),
            }}
          />
        }
      />

      <EntityDrawer
        open={!!openId}
        onOpenChange={(o) => !o && setOpenId(null)}
        title={detail.data?.name_en ?? "Legal Entity"}
        description={
          detail.data ? `CR ${detail.data.cr_number}` : undefined
        }
        width="lg"
      >
        {detail.data && (
          <LegalEntityDetailBody
            detail={detail.data}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ["legal-entity", openId] });
              qc.invalidateQueries({ queryKey: ["legal-entities"] });
            }}
            onArchived={() => {
              qc.invalidateQueries({ queryKey: ["legal-entities"] });
              setOpenId(null);
            }}
          />
        )}
      </EntityDrawer>

      {showAdd && (
        <AddLegalEntityModal
          onClose={() => setShowAdd(false)}
          onCreated={(created) => {
            qc.invalidateQueries({ queryKey: ["legal-entities"] });
            setShowAdd(false);
            setOpenId(created.id);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer body — tabs (Identity / Compliance / Banking / Stats)
// ---------------------------------------------------------------------------

type DetailTab = "identity" | "compliance" | "banking" | "stats";

function LegalEntityDetailBody({
  detail,
  onSaved,
  onArchived,
}: {
  detail: LegalEntityDetail;
  onSaved: () => void;
  onArchived: () => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<DetailTab>("identity");
  const [draft, setDraft] = useState({
    name_en: detail.name_en,
    name_ar: detail.name_ar,
    zatca_tax_id: detail.zatca_tax_id ?? "",
    registered_address: detail.registered_address ?? "",
    registered_city: detail.registered_city ?? "",
    hr_contact_email: detail.hr_contact_email ?? "",
    gosi_establishment_id: detail.gosi_establishment_id ?? "",
    qiwa_unified_number: detail.qiwa_unified_number ?? "",
    default_bank_account_name: detail.default_bank_account_name ?? "",
    default_bank_iban: detail.default_bank_iban ?? "",
  });
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.updateLegalEntity(detail.id, {
        ...draft,
        // Trim empty strings to null so we don't store noise.
        zatca_tax_id: draft.zatca_tax_id || null,
        registered_address: draft.registered_address || null,
        registered_city: draft.registered_city || null,
        hr_contact_email: draft.hr_contact_email || null,
        gosi_establishment_id: draft.gosi_establishment_id || null,
        qiwa_unified_number: draft.qiwa_unified_number || null,
        default_bank_account_name: draft.default_bank_account_name || null,
        default_bank_iban: draft.default_bank_iban || null,
      }),
    onSuccess: () => {
      setErr(null);
      onSaved();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.detail : String(e)),
  });

  const archive = useMutation({
    mutationFn: () => api.archiveLegalEntity(detail.id),
    onSuccess: () => onArchived(),
    onError: (e) =>
      setErr(e instanceof ApiError ? e.detail : String(e)),
  });

  const bandClass =
    _BANDS[detail.stats.saudization_band] ?? "bg-surface-sunken";

  return (
    <div className="flex flex-col gap-4">
      {/* Tab strip */}
      <div className="flex items-center gap-2 border-b border-border-subtle">
        {(
          [
            ["identity", "Identity", Building2],
            ["compliance", "Compliance", ShieldCheck],
            ["banking", "Banking", Banknote],
            ["stats", "Stats", FileText],
          ] as Array<[DetailTab, string, typeof Building2]>
        ).map(([k, label, Icon]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={[
              "inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px",
              tab === k
                ? "border-brand text-text-primary font-semibold"
                : "border-transparent text-text-tertiary hover:text-text-primary",
            ].join(" ")}
          >
            <Icon size={14} aria-hidden /> {label}
          </button>
        ))}
      </div>

      {err && (
        <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {err}
        </div>
      )}

      {tab === "identity" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ReadOnly label={t("app.le_field_cr_frozen")} value={detail.cr_number} mono />
          <ReadOnly label={t("app.le_field_region_frozen")} value={detail.region ?? "—"} />
          <Editable
            label={t("app.le_field_name_en")}
            value={draft.name_en}
            onChange={(v) => setDraft({ ...draft, name_en: v })}
          />
          <Editable
            label={t("app.le_field_name_ar")}
            value={draft.name_ar}
            dir="rtl"
            onChange={(v) => setDraft({ ...draft, name_ar: v })}
          />
          <Editable
            label={t("app.le_field_zatca")}
            value={draft.zatca_tax_id}
            onChange={(v) => setDraft({ ...draft, zatca_tax_id: v })}
          />
          <Editable
            label={t("app.le_field_hr_email")}
            type="email"
            value={draft.hr_contact_email}
            onChange={(v) => setDraft({ ...draft, hr_contact_email: v })}
          />
          <Editable
            label={t("app.le_field_city")}
            value={draft.registered_city}
            onChange={(v) => setDraft({ ...draft, registered_city: v })}
          />
          <Editable
            label={t("app.le_field_address")}
            value={draft.registered_address}
            onChange={(v) => setDraft({ ...draft, registered_address: v })}
          />
        </div>
      )}

      {tab === "compliance" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Editable
            label={t("app.le_field_gosi")}
            value={draft.gosi_establishment_id}
            onChange={(v) =>
              setDraft({ ...draft, gosi_establishment_id: v })
            }
          />
          <Editable
            label={t("app.le_field_qiwa")}
            value={draft.qiwa_unified_number}
            onChange={(v) =>
              setDraft({ ...draft, qiwa_unified_number: v })
            }
          />
          <div className="sm:col-span-2 rounded border border-border-subtle bg-surface-sunken px-3 py-2 text-xs text-text-secondary">
            <strong>{t("app.le_mudad_status")}</strong>{" "}
            <span className="text-text-tertiary">
              {t("app.le_mudad_coming")}
            </span>
          </div>
        </div>
      )}

      {tab === "banking" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Editable
            label={t("app.le_field_bank_name")}
            value={draft.default_bank_account_name}
            onChange={(v) =>
              setDraft({ ...draft, default_bank_account_name: v })
            }
          />
          <Editable
            label={t("app.le_field_iban")}
            value={draft.default_bank_iban}
            placeholder={t("app.le_field_iban_placeholder")}
            mono
            onChange={(v) =>
              setDraft({
                ...draft,
                default_bank_iban: v.replace(/\s+/g, "").toUpperCase(),
              })
            }
          />
        </div>
      )}

      {tab === "stats" && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label={t("app.le_stat_active_employees")} value={detail.stats.employee_count} />
          <StatCard
            label={t("app.le_stat_active_contracts")}
            value={detail.stats.active_contract_count}
          />
          <StatCard
            label={t("app.le_stat_saudi_nationals")}
            value={detail.stats.saudi_employee_count}
          />
          <div className="rounded-xl border border-border-subtle bg-surface-default p-3">
            <p className="text-xs text-text-secondary">{t("app.le_stat_saudization")}</p>
            <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-text-primary">
              {detail.stats.saudization_percent.toFixed(1)}%
            </p>
            <span
              className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${bandClass}`}
            >
              {detail.stats.saudization_band}
            </span>
          </div>
        </div>
      )}

      <footer className="mt-2 flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending || !!detail.archived_at}
          className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        {!detail.archived_at && (
          <button
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Archive "${detail.name_en}"? Refused if active employees / contracts exist.`,
                )
              ) {
                archive.mutate();
              }
            }}
            disabled={archive.isPending}
            className="ms-auto rounded border border-rose-200 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            Archive entity
          </button>
        )}
      </footer>

      {/* Event history */}
      <section className="mt-2 border-t border-border-subtle pt-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          History
        </h3>
        {detail.recent_events.length === 0 ? (
          <p className="text-xs text-text-tertiary">{t("app.le_no_events")}</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {detail.recent_events.map((e) => (
              <li
                key={e.id}
                className="flex items-baseline gap-2 text-xs"
              >
                <span className="font-mono text-text-tertiary">
                  {e.occurred_at.slice(0, 16).replace("T", " ")}
                </span>
                <span className="rounded bg-surface-sunken px-1.5 py-0.5 font-medium text-text-secondary">
                  {e.event_type}
                </span>
                <span className="text-text-tertiary">
                  {e.actor_clerk_user_id ?? "system"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const CR_NUMBER_RE = /^\d{10}$/;

function isValidCrNumber(value: string): boolean {
  return CR_NUMBER_RE.test(value.trim());
}

/** Map FastAPI/Pydantic create-body errors to the modal's required-fields copy. */
function isLegalEntityCreateValidationError(detail: string): boolean {
  if (/hr_contact_email/i.test(detail)) return true;
  return /(?:name_en|name_ar|cr_number)/i.test(detail);
}

// ---------------------------------------------------------------------------
// Add modal
// ---------------------------------------------------------------------------

function AddLegalEntityModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (created: LegalEntityDetail) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<LegalEntityCreateBody>({
    name_en: "",
    name_ar: "",
    cr_number: "",
    registered_city: "",
    region: "bahrain",
    hr_contact_email: "",
    gosi_establishment_id: "",
    qiwa_unified_number: "",
  });
  const [err, setErr] = useState<string | null>(null);

  const requiredMsg = t("app.le_new_required_msg");

  const create = useMutation({
    mutationFn: () => api.createLegalEntity(form),
    onSuccess: (created) => onCreated(created),
    onError: (e) => {
      if (e instanceof ApiError && isLegalEntityCreateValidationError(e.detail)) {
        setErr(requiredMsg);
        return;
      }
      setErr(e instanceof ApiError ? e.detail : String(e));
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("app.le_new_title")}
      width="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setErr(null);
              if (
                !form.name_en.trim() ||
                !form.name_ar.trim() ||
                !form.cr_number.trim() ||
                !(form.hr_contact_email ?? "").trim()
              ) {
                setErr(requiredMsg);
                return;
              }
              const cr = form.cr_number.trim();
              if (!isValidCrNumber(cr)) {
                setErr(
                  t("app.le_cr_exactly_10", {
                    value: form.cr_number.trim() || "—",
                  }),
                );
                return;
              }
              create.mutate();
            }}
            disabled={create.isPending}
            className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            {create.isPending
              ? t("policies.creating")
              : t("policies.create")}
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Editable
            label={t("app.le_field_name_en_req")}
            value={form.name_en}
            onChange={(v) => setForm({ ...form, name_en: v })}
          />
          <Editable
            label={t("app.le_field_name_ar_req")}
            value={form.name_ar}
            dir="rtl"
            onChange={(v) => setForm({ ...form, name_ar: v })}
          />
          <Editable
            label={t("app.le_field_cr_req")}
            value={form.cr_number}
            mono
            placeholder={t("app.le_field_cr_placeholder")}
            inputMode="numeric"
            maxLength={10}
            onChange={(v) => setForm({ ...form, cr_number: v })}
          />
          <Editable
            label={t("app.le_field_city")}
            value={form.registered_city ?? ""}
            onChange={(v) => setForm({ ...form, registered_city: v })}
          />
          <Editable
            label={t("app.le_field_hr_email_req")}
            type="email"
            value={form.hr_contact_email ?? ""}
            onChange={(v) => setForm({ ...form, hr_contact_email: v })}
          />
          <Editable
            label={t("app.le_field_gosi")}
            value={form.gosi_establishment_id ?? ""}
            onChange={(v) =>
              setForm({ ...form, gosi_establishment_id: v })
            }
          />
          <Editable
            label={t("app.le_field_qiwa")}
            value={form.qiwa_unified_number ?? ""}
            onChange={(v) =>
              setForm({ ...form, qiwa_unified_number: v })
            }
          />
      </div>
      {err && (
        <p className="mt-3 text-xs text-rose-700">{err}</p>
      )}
    </EntityDrawer>
  );
}

// ---------------------------------------------------------------------------
// Local field components
// ---------------------------------------------------------------------------

function Editable({
  label,
  value,
  onChange,
  type = "text",
  mono = false,
  dir,
  placeholder,
  inputMode,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  mono?: boolean;
  dir?: "ltr" | "rtl";
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      <input
        type={type}
        value={value}
        dir={dir}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className={`rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm placeholder:text-text-tertiary ${
          mono ? "font-mono" : ""
        }`}
      />
    </label>
  );
}

function ReadOnly({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-secondary">{label}</p>
      <p
        className={`mt-1 text-sm text-text-primary ${
          mono ? "font-mono" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-default p-3">
      <p className="text-xs text-text-secondary">{label}</p>
      <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-text-primary">
        {value}
      </p>
    </div>
  );
}
